import { useEffect, useRef, useState } from 'react';

export const moveItem = <T>(arr: T[], from: number, to: number): T[] => {
  const copy = [...arr];
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
};

// Drives a drag-and-drop reorderable list backed by a server "reorder" endpoint.
// Optimistically reorders local state on hover, then persists the new order when
// the drag ends (if the order actually changed and there are at least 2 items,
// which the backend reorder endpoints require).
export function useReorderableList<T extends { id: number }>(
  items: T[],
  onReorder: (ids: number[]) => void
) {
  const [ordered, setOrdered] = useState<T[]>(items);
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const committedIdsRef = useRef<string>(
    JSON.stringify(items.map((item) => item.id))
  );

  // Re-sync with server data, but never while a drag is in progress.
  useEffect(() => {
    if (draggedId === null) {
      setOrdered(items);
      committedIdsRef.current = JSON.stringify(items.map((item) => item.id));
    }
  }, [items]);

  const handleDragHover = (dragIndex: number, hoverIndex: number) => {
    setOrdered((prev) => moveItem(prev, dragIndex, hoverIndex));
  };

  const handleSetDraggedId = (id: number | null) => {
    setDraggedId(id);
    if (id === null) {
      const newIds = ordered.map((item) => item.id);
      if (
        newIds.length >= 2 &&
        JSON.stringify(newIds) !== committedIdsRef.current
      ) {
        committedIdsRef.current = JSON.stringify(newIds);
        onReorder(newIds);
      }
    }
  };

  return {
    ordered,
    setOrdered,
    draggedId,
    setDraggedId: handleSetDraggedId,
    handleDragHover,
  };
}
